/* ═══════════════════════════════════════════════════════════════════════
   cosmos-stars.js — THE DEFAULT COSMOS (v6.8.0)
   ───────────────────────────────────────────────────────────────────────
   The backdrop a visitor lands on. Three parallax star layers drifting
   under a warm nebula, with an occasional shooting star. No painting,
   no tier: this is the neutral state of the house.

   The paintings only enter once a visitor chooses a tier. From that
   moment html[data-art-mode] flips to "art", this renderer parks itself,
   and effects-tier.js takes the backdrop over.

   Contract
     • Draws into  <canvas id="cosmosStars">  inside #cosmos
     • Runs only while html[data-art-mode="cosmos"]
     • Pauses on tab blur, on reduced motion (renders one static frame),
       and whenever the art backdrop is showing
     • Device-pixel-ratio capped at 2 — a starfield is not worth 4x fill
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var canvas = document.getElementById('cosmosStars');
  if (!canvas || !canvas.getContext) return;

  var ctx = canvas.getContext('2d');
  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var W = 0, H = 0, DPR = 1;
  var stars = [];
  var shooters = [];
  var raf = 0;
  var running = false;
  var lastT = 0;
  var nextShooter = 2200;

  /* ── palette ───────────────────────────────────────────────────────
     Warm whites and ambers only. Nothing in the melanopsin band —
     the backdrop of a circadian brand should not itself be a 480nm
     light source on a visitor's screen at night. */
  var STAR_TINTS = [
    'rgba(255, 248, 235, ',   /* warm white   */
    'rgba(244, 226, 196, ',   /* cream        */
    'rgba(232, 162, 82, ',    /* amber-hi     */
    'rgba(212, 136, 58, '     /* amber        */
  ];

  function rand(a, b) { return a + Math.random() * (b - a); }

  /* ── field generation ─────────────────────────────────────────────
     Density scales with area so a 4K monitor and a phone read the same,
     with a hard ceiling so a very wide display cannot run away with it. */
  function buildField() {
    var area = W * H;
    var count = Math.min(420, Math.max(90, Math.round(area / 6600)));
    stars = [];
    for (var i = 0; i < count; i++) {
      /* depth 0 = far and faint and slow, 1 = near and bright and quick */
      var depth = Math.pow(Math.random(), 1.7);
      stars.push({
        x: Math.random() * W,
        y: Math.random() * H,
        r: 0.45 + depth * 1.5,
        a: 0.3 + depth * 0.62,
        tint: STAR_TINTS[(Math.random() * STAR_TINTS.length) | 0],
        vx: -(0.0018 + depth * 0.007),      /* px per ms, drifting left */
        vy: (0.0004 + depth * 0.0016),      /* and very slightly down   */
        tw: rand(0.0006, 0.0022),           /* twinkle rate             */
        ph: Math.random() * Math.PI * 2     /* twinkle phase            */
      });
    }
  }

  function resize() {
    DPR = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth || window.innerWidth;
    H = canvas.clientHeight || window.innerHeight;
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    buildField();
  }

  /* ── shooting stars ───────────────────────────────────────────────
     Rare, short, and always travelling down-left, so they read as one
     weather system rather than random noise. */
  function spawnShooter() {
    var startX = rand(W * 0.35, W * 1.05);
    var startY = rand(-H * 0.05, H * 0.45);
    shooters.push({
      x: startX,
      y: startY,
      vx: -rand(0.34, 0.52),
      vy: rand(0.26, 0.4),
      life: 0,
      span: rand(750, 1150),
      len: rand(90, 190)
    });
  }

  function drawShooter(s) {
    var t = s.life / s.span;
    /* fade in fast, fade out slow */
    var alpha = t < 0.12 ? t / 0.12 : (1 - (t - 0.12) / 0.88);
    if (alpha <= 0) return;
    alpha *= 0.85;

    var tailX = s.x - s.vx * s.len;
    var tailY = s.y - s.vy * s.len;
    var g = ctx.createLinearGradient(tailX, tailY, s.x, s.y);
    g.addColorStop(0, 'rgba(212, 136, 58, 0)');
    g.addColorStop(0.55, 'rgba(212, 136, 58, ' + (alpha * 0.35).toFixed(3) + ')');
    g.addColorStop(1, 'rgba(255, 248, 235, ' + alpha.toFixed(3) + ')');

    ctx.strokeStyle = g;
    ctx.lineWidth = 1.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(s.x, s.y);
    ctx.stroke();

    ctx.fillStyle = 'rgba(255, 248, 235, ' + alpha.toFixed(3) + ')';
    ctx.beginPath();
    ctx.arc(s.x, s.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  /* ── frame ────────────────────────────────────────────────────────── */
  function draw(dt, t) {
    ctx.clearRect(0, 0, W, H);

    for (var i = 0; i < stars.length; i++) {
      var s = stars[i];
      if (dt) {
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        if (s.x < -2) { s.x = W + 2; s.y = Math.random() * H; }
        if (s.y > H + 2) { s.y = -2; s.x = Math.random() * W; }
      }
      var twinkle = reduced ? 1 : 0.72 + 0.28 * Math.sin(s.ph + t * s.tw);
      ctx.fillStyle = s.tint + (s.a * twinkle).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    if (!reduced) {
      for (var j = shooters.length - 1; j >= 0; j--) {
        var sh = shooters[j];
        sh.life += dt;
        sh.x += sh.vx * dt;
        sh.y += sh.vy * dt;
        if (sh.life >= sh.span || sh.x < -260 || sh.y > H + 260) {
          shooters.splice(j, 1);
        } else {
          drawShooter(sh);
        }
      }
    }
  }

  function tick(now) {
    if (!running) return;
    var dt = lastT ? Math.min(now - lastT, 64) : 16;
    lastT = now;

    nextShooter -= dt;
    if (nextShooter <= 0 && shooters.length < 2) {
      spawnShooter();
      nextShooter = rand(5200, 11000);
    }

    draw(dt, now);
    raf = window.requestAnimationFrame(tick);
  }

  function start() {
    if (running || reduced) return;
    running = true;
    lastT = 0;
    raf = window.requestAnimationFrame(tick);
  }

  function stop() {
    running = false;
    if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
  }

  /* ── art-mode gate ────────────────────────────────────────────────
     "cosmos" is the landing state. Anything else means a painting has
     taken the backdrop, so this renderer stands down entirely. */
  function inCosmosMode() {
    return (root.getAttribute('data-art-mode') || 'cosmos') === 'cosmos';
  }

  function sync() {
    if (inCosmosMode() && !document.hidden) {
      canvas.style.opacity = '1';
      if (reduced) { draw(0, 0); }
      else { start(); }
    } else {
      stop();
      canvas.style.opacity = '0';
      /* Clear once the fade has finished, so a parked canvas costs nothing */
      window.setTimeout(function () {
        if (!inCosmosMode()) ctx.clearRect(0, 0, W, H);
      }, 1500);
    }
  }

  /* ── wiring ───────────────────────────────────────────────────────── */
  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      resize();
      if (reduced && inCosmosMode()) draw(0, 0);
    }, 180);
  }, { passive: true });

  document.addEventListener('visibilitychange', sync);
  window.addEventListener('artModeChanged', sync);

  /* Belt and braces: if anything flips the attribute without firing the
     event, pick it up anyway. */
  if (window.MutationObserver) {
    new MutationObserver(sync).observe(root, {
      attributes: true,
      attributeFilter: ['data-art-mode']
    });
  }

  resize();
  sync();
})();
